import { CategoryDto, MenuTypes } from "typing";
import { Dispatch, SetStateAction, useCallback } from "react";
import { useRouter } from "next/router";
import { buildTestIds, useCategories, useShallow } from "application";
import { ArrowLeftCategoriesIcon, ArrowRight, theme } from "ui";
import {
  SeeAll,
  SubCategoriesContainer,
  SubCategoriesHeader,
  SubCategoriesTitle,
  CategoryContainer,
  CategoryTitle,
} from "./styles";

interface SubCategoriesProps {
  onCloseSidebarMenu: () => void;
  level: MenuTypes;
  setCategoriesLevel: Dispatch<SetStateAction<MenuTypes>>;
}

const SubCategories = ({
  onCloseSidebarMenu,
  level,
  setCategoriesLevel,
}: SubCategoriesProps) => {
  const router = useRouter();
  const {
    selectedCategory,
    categoryTitle,
    categoryUrl,
    setThirdLevelCategory,
    setThirdLevelTitle,
    setThirdLevelUrl,
    thirdLevelCategory,
    thirdLevelTitle,
    thirdLevelUrl,
  } = useCategories(
    useShallow((state) => ({
      selectedCategory: state.selectedCategory,
      categoryTitle: state.categoryTitle,
      categoryUrl: state.categoryUrl,
      setThirdLevelCategory: state.setThirdLevelCategory,
      setThirdLevelTitle: state.setThirdLevelTitle,
      setThirdLevelUrl: state.setThirdLevelUrl,
      thirdLevelCategory: state.thirdLevelCategory,
      thirdLevelTitle: state.thirdLevelTitle,
      thirdLevelUrl: state.thirdLevelUrl,
    }))
  );

  const currentTitle =
    level === "SecondLevel" ? categoryTitle : thirdLevelTitle;

  const currentUrl = level === "SecondLevel" ? categoryUrl : thirdLevelUrl;

  const currentCategory =
    level === "SecondLevel" ? selectedCategory : thirdLevelCategory;

  const handleCategoryClick = useCallback(
    (title: string, url: string, category?: CategoryDto[]) => {
      if (category?.length && level === "SecondLevel") {
        setThirdLevelCategory(category);
        setThirdLevelTitle(title);
        setThirdLevelUrl(url);
        setCategoriesLevel("ThirdLevel");
        return;
      }

      router.push(`/${url}`);
      onCloseSidebarMenu();
    },
    [
      level,
      router,
      onCloseSidebarMenu,
      setThirdLevelCategory,
      setThirdLevelTitle,
      setThirdLevelUrl,
      setCategoriesLevel,
    ]
  );

  const handleChangeLevel = useCallback(() => {
    if (level === "ThirdLevel") {
      setCategoriesLevel("SecondLevel");
      return;
    }

    setCategoriesLevel("FirstLevel");
  }, [level, setCategoriesLevel]);

  return (
    <SubCategoriesContainer {...buildTestIds("sub-categories-container")}>
      <SubCategoriesHeader
        {...buildTestIds("sub-categories-header")}
        onClick={handleChangeLevel}
      >
        <ArrowLeftCategoriesIcon />
        <SubCategoriesTitle {...buildTestIds("sub-categories-title")}>
          {currentTitle?.toUpperCase()}
        </SubCategoriesTitle>
      </SubCategoriesHeader>
      <SeeAll
        {...buildTestIds(
          `see-everything-on-category-${currentTitle}-container`
        )}
      >
        <CategoryTitle
          onClick={() => {
            router.push(`/${currentUrl}`);
            onCloseSidebarMenu();
          }}
          {...buildTestIds(`see-everything-on-category-${currentTitle}`)}
        >
          Tudo em {currentTitle}
        </CategoryTitle>
        <ArrowRight
          width={20}
          height={20}
          color={theme.colors.primary["150"]}
        />
      </SeeAll>
      {currentCategory?.map((category) => (
        <CategoryContainer
          {...buildTestIds(`category-container-${category?.title}`)}
          onClick={() =>
            handleCategoryClick(
              category?.title,
              category?.descriptionUrl,
              category?.categories
            )
          }
          key={category?.id}
        >
          <CategoryTitle {...buildTestIds(`category-title-${category?.title}`)}>
            {category?.title}
          </CategoryTitle>
          <ArrowRight
            width={20}
            height={20}
            color={theme.colors.neutral["680"]}
          />
        </CategoryContainer>
      ))}
    </SubCategoriesContainer>
  );
};

export { SubCategories };
